import { useCallback, useEffect, useRef, useState } from 'react';
import { Switch, Tooltip, Typography } from 'antd';
import { useCan } from '../hooks/usePermissions';
import * as api from '../lib/api';
import { useToast } from './Toast';

interface Props {
  /** The channel whose Borgee-owned turn-routing policy is being changed. */
  channelId: string;
  /** DMs and categories have no turn-routing policy. */
  eligible: boolean;
}

/**
 * A compact per-channel policy control mounted immediately above the channel
 * roster. It is not a generic settings preference: only members already
 * allowed to manage the channel can see or mutate it.
 */
export default function TurnRoutingPolicyControl({ channelId, eligible }: Props) {
  const canManage = useCan('channel.manage_visibility', channelId);
  const { showToast } = useToast();
  const requestEpoch = useRef(0);
  const [policy, setPolicy] = useState<
    | { channelId: string; status: 'loading' | 'error' }
    | { channelId: string; status: 'ready' | 'saving'; enabled: boolean }
  >({ channelId, status: 'loading' });

  const currentPolicy =
    policy.channelId === channelId ? policy : ({ channelId, status: 'loading' } as const);

  useEffect(() => {
    const epoch = ++requestEpoch.current;
    if (!eligible) return;
    setPolicy({ channelId, status: 'loading' });
    void api
      .getTurnRoutingPolicy(channelId)
      .then((policy) => {
        if (requestEpoch.current === epoch) {
          setPolicy({ channelId, status: 'ready', enabled: policy.enabled });
        }
      })
      .catch(() => {
        if (requestEpoch.current === epoch) {
          setPolicy({ channelId, status: 'error' });
          showToast('Failed to load Borgee turn routing');
        }
      });
    return () => {
      requestEpoch.current++;
    };
  }, [channelId, eligible, canManage, showToast]);

  const toggle = useCallback(
    async (next: boolean) => {
      if (currentPolicy.status !== 'ready') return;
      const epoch = ++requestEpoch.current;
      const previous = currentPolicy.enabled;
      setPolicy({ channelId, status: 'saving', enabled: previous });
      try {
        const policy = await api.setTurnRoutingPolicy(channelId, next);
        if (requestEpoch.current === epoch) {
          setPolicy({ channelId, status: 'ready', enabled: policy.enabled });
        }
      } catch (err) {
        if (requestEpoch.current === epoch) {
          setPolicy({ channelId, status: 'ready', enabled: previous });
          showToast(err instanceof Error ? err.message : 'Failed to update Borgee turn routing');
        }
      }
    },
    [channelId, currentPolicy, showToast],
  );

  if (!eligible) return null;

  return (
    <div className="turn-routing-policy-control" data-testid="turn-routing-policy-control">
      <div className="turn-routing-policy-control-row">
        <div>
          <Typography.Text strong style={{ fontSize: 12 }}>
            Borgee turn routing
          </Typography.Text>
          <Tooltip title="Borgee decides when eligible agents receive a turn in this channel.">
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
              Borgee decides eligible turns
            </Typography.Text>
          </Tooltip>
        </div>
        <Switch
          size="small"
          checked={
            currentPolicy.status === 'ready' || currentPolicy.status === 'saving'
              ? currentPolicy.enabled
              : false
          }
          loading={currentPolicy.status === 'loading' || currentPolicy.status === 'saving'}
          disabled={currentPolicy.status !== 'ready' || !canManage}
          onChange={toggle}
          aria-label="Enable Borgee turn routing for this channel"
        />
      </div>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', fontSize: 11, lineHeight: 1.35, marginTop: 4 }}
      >
        Borgee decides when eligible agents receive a turn. In 1H+1A with mentions off, ordinary human messages give the agent a turn; otherwise explicit @ targets do. Threads have independent policies.
      </Typography.Text>
    </div>
  );
}
